import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';

import { LoginPage } from './pages/LoginPage';
import { LandingPage } from './pages/LandingPage';
import { SignupPage } from './pages/SignupPage';
import { AuthCallback } from './pages/AuthCallback';
import { OnboardingWizard } from './pages/OnboardingWizard';
import { BillingPage } from './pages/BillingPage';
import { PlatformPlansPage } from './pages/PlatformPlansPage';
import { PlatformStripePage } from './pages/PlatformStripePage';
import { PrivacidadePage } from './pages/PrivacidadePage';
import { TermosPage } from './pages/TermosPage';
import { DashboardPage } from './pages/DashboardPage';
import { DashboardGeralPage } from './pages/DashboardGeralPage';
import { FinanceiroPlanejamentoPage } from './pages/FinanceiroPlanejamentoPage';
import { FinanceiroGastosPage } from './pages/FinanceiroGastosPage';
import {
  FinanceiroDashboardPage,
  FinanceiroAnalisePage,
} from './pages/FinanceiroAnaliseDashboard';
import { FinanceiroPrestacaoPage } from './pages/FinanceiroPrestacaoPage';
import { LiderancasPage } from './pages/LiderancasPage';
import { ApoiadoresPage } from './pages/ApoiadoresPage';
import { LinksPage } from './pages/LinksPage';
import { DestinosPage } from './pages/DestinosPage';
import { CartoesPage } from './pages/CartoesPage';
import { RankingPage } from './pages/RankingPage';
import MetasPage from './pages/MetasPage';
import { RelatoriosPage } from './pages/RelatoriosPage';
import { UsuariosPage } from './pages/UsuariosPage';
import { CapturePage } from './pages/CapturePage';
import { ConfiguracoesPage } from './pages/ConfiguracoesPage';
import { VerifyInvitationPage } from './pages/VerifyInvitationPage';
import { PlatformPage } from './pages/PlatformPage';
import { CreateTenantPage } from './pages/CreateTenantPage';
import { PublicRedirectPage } from './pages/PublicRedirectPage';
import { LogsPage } from './pages/LogsPage';
import { LeaderSignupPage } from './pages/LeaderSignupPage';
import { DobradasPage } from './pages/DobradasPage';
import { InsightsPage } from './pages/InsightsPage';
import { PlatformInsightsPage } from './pages/PlatformInsightsPage';

import './App.css';

function AppRouter() {
  const location = useLocation();
  // Synchronous detection prevents race conditions with AuthProvider's /auth/me
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Auth */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/verify" element={<VerifyInvitationPage />} />
      <Route path="/privacidade" element={<PrivacidadePage />} />
      <Route path="/termos" element={<TermosPage />} />

      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingWizard />
          </ProtectedRoute>
        }
      />

      {/* Public tenant URLs: /{slug}/r/{token} and /{slug}/c/{token} */}
      <Route path="/:slug/r/:token" element={<PublicRedirectPage />} />
      <Route path="/:slug/c/:token" element={<CapturePage />} />
      <Route path="/:slug/cadastrar" element={<LeaderSignupPage />} />
      <Route path="/:slug/cl/:token" element={<LeaderSignupPage mode="invite" />} />
      <Route path="/:slug/d/:dobrada_slug/cadastrar" element={<LeaderSignupPage mode="dobrada" />} />

      {/* Super admin platform */}
      <Route
        path="/platform"
        element={
          <ProtectedRoute requireTenant={false} superAdminOnly>
            <PlatformPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform/tenants/new"
        element={
          <ProtectedRoute requireTenant={false} superAdminOnly>
            <CreateTenantPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform/plans"
        element={
          <ProtectedRoute requireTenant={false} superAdminOnly>
            <PlatformPlansPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform/insights"
        element={
          <ProtectedRoute requireTenant={false} superAdminOnly>
            <PlatformInsightsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/platform/stripe"
        element={
          <ProtectedRoute requireTenant={false} superAdminOnly>
            <PlatformStripePage />
          </ProtectedRoute>
        }
      />

      {/* Tenant-scoped */}
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardGeralPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mobilizacao/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financeiro/dashboard"
        element={
          <ProtectedRoute>
            <FinanceiroDashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financeiro/planejamento"
        element={
          <ProtectedRoute>
            <FinanceiroPlanejamentoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financeiro/gastos"
        element={
          <ProtectedRoute>
            <FinanceiroGastosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financeiro/analise"
        element={
          <ProtectedRoute>
            <FinanceiroAnalisePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/financeiro/prestacao"
        element={
          <ProtectedRoute>
            <FinanceiroPrestacaoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/mobilizacao/dobradas"
        element={
          <ProtectedRoute>
            <DobradasPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/liderancas"
        element={
          <ProtectedRoute>
            <LiderancasPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/apoiadores"
        element={
          <ProtectedRoute>
            <ApoiadoresPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/links"
        element={
          <ProtectedRoute>
            <LinksPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/destinos"
        element={
          <ProtectedRoute>
            <DestinosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/insights"
        element={
          <ProtectedRoute>
            <InsightsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cartoes"
        element={
          <ProtectedRoute>
            <CartoesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ranking"
        element={
          <ProtectedRoute>
            <RankingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/metas"
        element={
          <ProtectedRoute>
            <MetasPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/relatorios"
        element={
          <ProtectedRoute>
            <RelatoriosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/usuarios"
        element={
          <ProtectedRoute>
            <UsuariosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/configuracoes"
        element={
          <ProtectedRoute>
            <ConfiguracoesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/logs"
        element={
          <ProtectedRoute>
            <LogsPage />
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<LandingPage />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster />
    </AuthProvider>
  );
}

export default App;
